import { cloneDeep, get, union } from 'lodash';
import { TextAlignment } from '../../../../../common/table/CellTypeConstants';
import { CellData } from '../../../../../common/table/TableTypes';
import { getColumnCount, getRowCount } from '../../../../../common/table/utils/tableCellDataPropertyUtils';
import { getCellRangeFromStartAndSize } from '../tableCellRangeUtils';
import { forEachCellInRange } from './tableCellIterationUtils';

const normaliseCellFormat = (cellFormat: Partial<CellData>): Partial<CellData> => {
    const normalisedCellFormat = { ...cellFormat };

    if (normalisedCellFormat.background === undefined) delete normalisedCellFormat.background;
    if (normalisedCellFormat.textAlignment === undefined) delete normalisedCellFormat.textAlignment;
    if (!normalisedCellFormat.textStyle?.length) delete normalisedCellFormat.textStyle;

    return normalisedCellFormat;
};

// FIXME-TABLES - The logic here looks like it should be broken down into sub-functions with less nesting
const getCellFormatFromNeighbours = (
    row: number,
    col: number,
    dataArray: Array<Array<CellData>>,
    numberAdded: number,
    isAddingRows: boolean,
): Partial<CellData> => {
    const twoBefore = isAddingRows ? { row: row - 2, col } : { row, col: col - 2 };
    const oneBefore = isAddingRows ? { row: row - 1, col } : { row, col: col - 1 };
    const oneAfter = isAddingRows ? { row: row + numberAdded, col } : { row, col: col + numberAdded };
    const twoAfter = isAddingRows ? { row: row + numberAdded + 1, col } : { row, col: col + numberAdded + 1 };

    const twoBeforeCell = get(dataArray, [twoBefore.row, twoBefore.col]);
    const oneBeforeCell = get(dataArray, [oneBefore.row, oneBefore.col]);
    const oneAfterCell = get(dataArray, [oneAfter.row, oneAfter.col]);
    const twoAfterCell = get(dataArray, [twoAfter.row, twoAfter.col]);

    const nRows = getRowCount(dataArray);
    const nCols = getColumnCount(dataArray);

    const previousCount = isAddingRows ? nRows - numberAdded : nCols - numberAdded;

    const newCellDetails: { background?: string | null; textStyle?: string[]; textAlignment?: TextAlignment } = {};

    if (oneBeforeCell) {
        const {
            background: beforeBackground,
            textStyle: beforeTextStyle,
            textAlignment: beforeTextAlignment,
        } = oneBeforeCell;

        if (previousCount === 1) {
            return normaliseCellFormat({ background: beforeBackground, textStyle: beforeTextStyle });
        }

        if (twoBeforeCell?.background === beforeBackground || oneAfterCell?.background === beforeBackground) {
            newCellDetails.background = beforeBackground;
        }

        if (
            twoBeforeCell?.textAlignment === beforeTextAlignment ||
            oneAfterCell?.textAlignment === beforeTextAlignment
        ) {
            newCellDetails.textAlignment = beforeTextAlignment;
        }

        newCellDetails.textStyle = beforeTextStyle?.filter(
            (style) => twoBeforeCell?.textStyle?.includes(style) || oneAfterCell?.textStyle?.includes(style),
        );
    }

    if (oneAfterCell) {
        const {
            background: afterBackground,
            textStyle: afterTextStyle,
            textAlignment: afterTextAlignment,
        } = oneAfterCell;

        if (previousCount === 1) {
            return normaliseCellFormat({ background: afterBackground, textStyle: afterTextStyle });
        }

        if (twoAfterCell?.background === afterBackground && !newCellDetails.background) {
            newCellDetails.background = afterBackground;
        }

        if (twoAfterCell?.textAlignment === afterTextAlignment && !newCellDetails.textAlignment) {
            newCellDetails.textAlignment = afterTextAlignment;
        }

        const textStylesFromCellsAfter = afterTextStyle?.filter((style) => twoAfterCell?.textStyle?.includes(style));
        newCellDetails.textStyle = union(newCellDetails.textStyle, textStylesFromCellsAfter);
    }

    return normaliseCellFormat(newCellDetails);
};

/**
 * Continues neighbouring cell formatting onto newly inserted rows or columns in serialized table data.
 */
export const continueCellFormatting = ({
    dataArray,
    colIndex,
    nColsAdded,
    rowIndex,
    nRowsAdded,
}: {
    dataArray: Array<Array<CellData>>;
    colIndex: number;
    nColsAdded: number;
    rowIndex: number;
    nRowsAdded: number;
}): Array<Array<CellData>> => {
    const newData = cloneDeep(dataArray);

    const nRows = getRowCount(newData);
    const nCols = getColumnCount(newData);

    if (nColsAdded > 0) {
        forEachCellInRange({
            cellRange: getCellRangeFromStartAndSize({
                start: { row: 0, col: colIndex },
                size: { row: nRows, col: nColsAdded },
            }),
            callback: ({ row, col }) => {
                newData[row][col] = {
                    ...getCellFormatFromNeighbours(row, col, newData, nColsAdded, false),
                    ...newData[row][col],
                };
            },
        });
    }

    if (nRowsAdded > 0) {
        forEachCellInRange({
            cellRange: getCellRangeFromStartAndSize({
                start: { row: rowIndex, col: 0 },
                size: { row: nRowsAdded, col: nCols },
            }),
            callback: ({ row, col }) => {
                newData[row][col] = {
                    ...getCellFormatFromNeighbours(row, col, newData, nRowsAdded, true),
                    ...newData[row][col],
                };
            },
        });
    }

    return newData;
};
